"use client";

// Floating cookie notice — reference-build mechanic: appears bottom-left
// after a 700 ms delay (so it doesn't fight the hero intro), collapses to a
// small "Cookies" chip once the hero leaves the viewport, and re-expands on
// chip click or on scrolling back up. The choice lands in localStorage;
// both buttons carry equal visual weight (GDPR: rejecting must be as easy
// as accepting).
import Link from "next/link";
import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n/I18nProvider";

const STORAGE_KEY = "ecocloud.cookies.v1";

export type CookieChoice = "all" | "essential";

export function getCookieChoice(): CookieChoice | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "all" || v === "essential" ? v : null;
  } catch {
    return null;
  }
}

export function CookieNotice() {
  const t = useI18n().t.cookies;
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [heroInView, setHeroInView] = useState(true);
  const [userExpanded, setUserExpanded] = useState(false);

  // Delayed mount: nothing renders during SSR or the hero entrance.
  useEffect(() => {
    if (getCookieChoice()) return;
    const t = window.setTimeout(() => setMounted(true), 700);
    return () => window.clearTimeout(t);
  }, []);

  // Collapse to the chip once the hero scrolls away; scrolling back resets
  // the manual expansion so the card doesn't chase the reader down the page.
  useEffect(() => {
    if (!mounted) return;
    const hero = document.querySelector(".lp-hero");
    if (!hero || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        const inView = entries[0]?.isIntersecting ?? true;
        setHeroInView(inView);
        if (inView) setUserExpanded(false);
      },
      { threshold: 0.1 },
    );
    io.observe(hero);
    return () => io.disconnect();
  }, [mounted]);

  if (!mounted || dismissed) return null;

  const choose = (choice: CookieChoice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // storage unavailable — treat as session-only dismissal
    }
    setDismissed(true);
  };

  const asChip = !heroInView && !userExpanded;

  return (
    <div className={`lp-cookie ${asChip ? "is-chip" : ""}`}>
      <div
        className="lp-cookie-card"
        role="region"
        aria-label={t.region}
        aria-hidden={asChip}
        inert={asChip || undefined}
      >
        <span className="lp-cookie-h">{t.title}</span>
        <p>
          {t.body}{" "}
          <Link href="/docs" className="lp-cookie-more">
            {t.more}
          </Link>
        </p>
        <div className="lp-cookie-row">
          <button type="button" onClick={() => choose("essential")}>
            {t.essential}
          </button>
          <button
            type="button"
            className="lp-cookie-all"
            onClick={() => choose("all")}
          >
            {t.all}
          </button>
        </div>
      </div>
      <button
        type="button"
        className="lp-cookie-chip"
        aria-hidden={!asChip}
        inert={!asChip || undefined}
        onClick={() => setUserExpanded(true)}
      >
        {t.chip}
      </button>
    </div>
  );
}
